// ABOUTME: Page with a form to create a new game from a square pack.
// ABOUTME: Loads available packs; on submit creates the game and navigates to its manage page.
import { useState, useEffect } from 'react'
import { useAuth } from '@clerk/react'
import { useNavigate, useSearchParams } from 'react-router'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import { FormCard, FormCardHeader, FormCardBody, FormCardActions, InlineButton } from '../theme'
import TextField from '@mui/material/TextField'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import InputLabel from '@mui/material/InputLabel'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import Switch from '@mui/material/Switch'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import {
  squarePacksControllerList,
  squarePacksControllerCreate,
  squarePacksControllerGetOne,
  gameActionsControllerCreateGame,
  CreateGameDtoWinCondition,
  type SquarePackDto,
} from '../api/generated'
import { trackGameCreated } from '../analytics/events'

export function CreateGamePage() {
  const { getToken } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const sharedPackId = searchParams.get('squarePackId')
  const [sharedPack, setSharedPack] = useState<SquarePackDto | null>(null)
  const [packs, setPacks] = useState<SquarePackDto[]>([])
  const [title, setTitle] = useState('')
  const [packId, setPackId] = useState('')
  const [winCondition, setWinCondition] = useState<string>(CreateGameDtoWinCondition.LINE)
  const [automatic, setAutomatic] = useState(false)
  const [newCollectionOpen, setNewCollectionOpen] = useState(false)
  const [newCollectionTitle, setNewCollectionTitle] = useState('')
  const [newCollectionRandomCalled, setNewCollectionRandomCalled] = useState(false)
  const [titleError, setTitleError] = useState(false)
  const [newCollectionTitleError, setNewCollectionTitleError] = useState(false)

  useEffect(() => {
    if (!sharedPackId) return
    async function loadSharedPack() {
      const token = await getToken()
      const res = await squarePacksControllerGetOne(
        sharedPackId!,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      setSharedPack(res.data)
      setPackId(res.data.id)
    }
    loadSharedPack()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedPackId])

  useEffect(() => {
    async function load() {
      if (sharedPackId) return
      const token = await getToken()
      const res = await squarePacksControllerList(
        { page: 1, limit: 50 },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      const items = res.data.items as unknown as SquarePackDto[]
      setPacks(items)
      if (items.length > 0 && !sharedPackId) setPackId(items[0].id)
    }
    load()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedPackId])

  async function handleCreateCollection() {
    if (!newCollectionTitle.trim()) {
      setNewCollectionTitleError(true)
      return
    }
    const token = await getToken()
    const res = await squarePacksControllerCreate(
      { title: newCollectionTitle, randomCalled: newCollectionRandomCalled },
      { headers: { Authorization: `Bearer ${token}` } },
    )
    if (res.status === 201) {
      setPacks((prev) => [...prev, res.data])
      setPackId(res.data.id)
      setNewCollectionTitle('')
      setNewCollectionRandomCalled(false)
      setNewCollectionTitleError(false)
      setNewCollectionOpen(false)
    }
  }

  async function handleSubmit() {
    if (!title.trim()) {
      setTitleError(true)
      return
    }
    const token = await getToken()
    const res = await gameActionsControllerCreateGame(
      {
        title,
        squarePackId: packId,
        winCondition: winCondition as typeof CreateGameDtoWinCondition[keyof typeof CreateGameDtoWinCondition],
        automatic,
      },
      { headers: { Authorization: `Bearer ${token}` } },
    )
    if (res.status === 201) {
      trackGameCreated(res.data.id)
      navigate(`/games/${res.data.id}/manage`)
    }
  }

  return (
    <Box pt={4}>
      <Dialog open={newCollectionOpen} onClose={() => { setNewCollectionOpen(false); setNewCollectionRandomCalled(false); setNewCollectionTitleError(false) }}>
        <DialogTitle>New Square Pack</DialogTitle>
        <DialogContent>
          <TextField
            label="Square Pack Name"
            value={newCollectionTitle}
            onChange={(e) => { setNewCollectionTitle(e.target.value); setNewCollectionTitleError(false) }}
            fullWidth
            autoFocus
            error={newCollectionTitleError}
            helperText={newCollectionTitleError ? 'Square pack name is required' : undefined}
            inputProps={{ 'aria-label': 'square pack name' }}
          />
          <FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={newCollectionRandomCalled}
                  onChange={(e) => setNewCollectionRandomCalled(e.target.checked)}
                  inputProps={{ 'aria-label': 'random-called' }}
                />
              }
              label="Random Called"
            />
            <FormHelperText>Squares are called randomly. Turn off for host-pick mode (you select which square to call).</FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setNewCollectionOpen(false); setNewCollectionRandomCalled(false); setNewCollectionTitleError(false) }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateCollection}>Create</Button>
        </DialogActions>
      </Dialog>
      <FormCard>
        <FormCardHeader>
          <Typography variant="h4" component="h1">Create Game</Typography>
        </FormCardHeader>
        <FormCardBody>
          <Stack spacing={3}>
            <TextField
              label="Title"
              value={title}
              onChange={(e) => { setTitle(e.target.value); setTitleError(false) }}
              inputProps={{ 'aria-label': 'title' }}
              error={titleError}
              helperText={titleError ? 'Title is required' : undefined}
              fullWidth
            />
            {sharedPack ? (
              <Box>
                <Typography variant="body2" color="text.secondary">Square Pack</Typography>
                <Typography>{sharedPack.title}</Typography>
              </Box>
            ) : (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
                <FormControl fullWidth>
                  <InputLabel id="collection-label">Square Pack</InputLabel>
                  <Select
                    labelId="collection-label"
                    label="Square Pack"
                    value={packId}
                    onChange={(e) => setPackId(e.target.value)}
                  >
                    {packs.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <InlineButton
                  variant="outlined"
                  onClick={() => setNewCollectionOpen(true)}
                >
                  New Square Pack
                </InlineButton>
              </Stack>
            )}
            <FormControl fullWidth>
              <InputLabel id="win-label">Win Condition</InputLabel>
              <Select
                labelId="win-label"
                label="Win Condition"
                value={winCondition}
                onChange={(e) => setWinCondition(e.target.value)}
              >
                <MenuItem value={CreateGameDtoWinCondition.LINE}>Line</MenuItem>
                <MenuItem value={CreateGameDtoWinCondition.BLACKOUT}>Blackout</MenuItem>
                <MenuItem value={CreateGameDtoWinCondition.FOUR_CORNERS}>Four Corners</MenuItem>
              </Select>
            </FormControl>
            <FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={automatic}
                    onChange={(e) => setAutomatic(e.target.checked)}
                    inputProps={{ 'aria-label': 'automatic' }}
                  />
                }
                label="Automatic"
              />
              <FormHelperText>Squares are daubed automatically when called, and the game ends when someone wins.</FormHelperText>
            </FormControl>
            <FormCardActions>
              <Button variant="contained" onClick={handleSubmit}>Create Game</Button>
            </FormCardActions>
          </Stack>
        </FormCardBody>
      </FormCard>
    </Box>
  )
}
